import { useState, useEffect } from "react";
import { PageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  GraduationCap, 
  Users,
  MessageSquare,
  Heart,
  Calendar,
  PlusCircle
} from "lucide-react";

const Alumni = () => {
  const { user } = useAuth();
  const [alumni, setAlumni] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState({ title: "", content: "", post_type: "general" });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [alumniRes, postsRes] = await Promise.all([
        fetch(`${API}/alumni`, { credentials: 'include' }),
        fetch(`${API}/alumni/posts`, { credentials: 'include' })
      ]);

      if (alumniRes.ok) setAlumni(await alumniRes.json());
      if (postsRes.ok) setPosts(await postsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createPost = async () => {
    if (!newPost.title || !newPost.content) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await fetch(`${API}/alumni/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newPost)
      });

      if (response.ok) {
        toast.success("Post created successfully!");
        setNewPost({ title: "", content: "", post_type: "general" });
        setIsDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create post");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const getPostTypeColor = (type) => {
    const colors = {
      general: "badge-gold",
      job: "badge-success",
      event: "badge-warning",
      announcement: "badge-error"
    };
    return colors[type] || "badge-gold";
  };

  if (loading) {
    return (
      <PageLayout title="Alumni Association">
        <div className="flex items-center justify-center py-20">
          <div className="spinner" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Alumni Association"
      subtitle="Connect with fellow Aspiritech Academy graduates."
    >
      <Tabs defaultValue="feed" className="space-y-8" data-testid="alumni-page">
        <div className="flex items-center justify-between">
          <TabsList className="bg-surface-muted border border-slate-200">
            <TabsTrigger value="feed" className="data-[state=active]:bg-brand-gold data-[state=active]:text-black">
              Community Feed
            </TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-brand-gold data-[state=active]:text-black">
              Members
            </TabsTrigger>
          </TabsList>

          {user?.is_alumni && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-post-btn">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Create Post
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200">
                <DialogHeader>
                  <DialogTitle className="text-slate-900">Create a New Post</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Input
                      placeholder="Post title..."
                      value={newPost.title}
                      onChange={(e) => setNewPost(prev => ({ ...prev, title: e.target.value }))}
                      className="input-base"
                      data-testid="post-title-input"
                    />
                  </div>
                  <div>
                    <Select onValueChange={(value) => setNewPost(prev => ({ ...prev, post_type: value }))}>
                      <SelectTrigger className="input-base" data-testid="post-type-select">
                        <SelectValue placeholder="Post type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="job">Job Opportunity</SelectItem>
                        <SelectItem value="event">Event</SelectItem>
                        <SelectItem value="announcement">Announcement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Textarea
                      placeholder="Write your post content..."
                      value={newPost.content}
                      onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
                      className="input-base min-h-[150px]"
                      data-testid="post-content-input"
                    />
                  </div>
                  <Button onClick={createPost} className="w-full btn-primary" data-testid="submit-post-btn">
                    Publish Post
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Community Feed */}
        <TabsContent value="feed" className="space-y-6">
          {!user?.is_alumni && (
            <Card className="card-surface border-[#FFD507]/30">
              <CardContent className="py-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-gold/20 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Join the Alumni Association</p>
                    <p className="text-sm text-slate-500">
                      Complete your courses to become an alumni and unlock community features.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {posts.length > 0 ? (
            <div className="space-y-6">
              {posts.map((post) => (
                <Card key={post.post_id} className="card-surface" data-testid={`post-${post.post_id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4 mb-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={post.author_picture} />
                        <AvatarFallback className="bg-[#1E3A8A] text-slate-900">
                          {post.author_name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-slate-900">{post.author_name}</p>
                          <span className={`px-2 py-0.5 text-xs rounded ${getPostTypeColor(post.post_type)}`}>
                            {post.post_type}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <h3 className="text-lg font-medium text-slate-900 mb-2">{post.title}</h3>
                    <p className="text-slate-600 whitespace-pre-wrap">{post.content}</p>

                    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-200">
                      <button className="flex items-center gap-2 text-slate-500 hover:text-brand-navy transition-colors">
                        <Heart className="w-5 h-5" />
                        <span className="text-sm">{post.likes || 0}</span>
                      </button>
                      <button className="flex items-center gap-2 text-slate-500 hover:text-brand-navy transition-colors">
                        <MessageSquare className="w-5 h-5" />
                        <span className="text-sm">Comment</span>
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-surface">
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Posts Yet</h3>
                <p className="text-slate-500">
                  Be the first to share something with the community!
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Members */}
        <TabsContent value="members" className="space-y-6">
          {alumni.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {alumni.map((member) => (
                <Card 
                  key={member.user_id} 
                  className="card-surface card-hover"
                  data-testid={`alumni-card-${member.user_id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4 mb-4">
                      <Avatar className="h-16 w-16 border-2 border-[#FFD507]/30">
                        <AvatarImage src={member.picture} />
                        <AvatarFallback className="bg-[#1E3A8A] text-slate-900 text-xl">
                          {member.name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-medium text-slate-900">{member.name}</h3>
                        <p className="text-sm text-slate-500">{member.email}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-brand-gold/20 text-brand-navy rounded">
                          Alumni
                        </span>
                      </div>
                    </div>

                    {member.bio && (
                      <p className="text-sm text-slate-500 line-clamp-2">{member.bio}</p>
                    )}

                    {member.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {member.skills.slice(0, 3).map((skill, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 text-xs bg-[#1E3A8A]/30 text-[#3B82F6] rounded"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-surface">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Alumni Yet</h3>
                <p className="text-slate-500">
                  Complete your courses to become part of the alumni network!
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </PageLayout>
  );
};

export default Alumni;
